import { compareStringsAccentInsensitive } from '../../../../../shared/util/compare'
import type { SimpleLocation } from '../types'

export type TamLocationMatchKind = 'exact' | 'fuzzy' | 'none'

export interface TamLocationMatch {
  kind: TamLocationMatchKind
  location: SimpleLocation | null
}

function compactWellToken(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Z0-9]/g, '')
}

/** Prefijo + sufijo numérico/alfanumérico, p. ej. LB 0041 → { prefix: LB, suffix: 0041 } */
function parseWellParts(value: string): { prefix: string; suffix: string } | null {
  const cleaned = value.trim().toUpperCase().replace(/\s+/g, ' ')
  const spaced = cleaned.match(/^([A-Z]{1,6})\s+(\S+)$/)
  if (spaced) return { prefix: spaced[1], suffix: spaced[2] }
  const glued = cleaned.match(/^([A-Z]{1,6})(\d[\dA-Z]*)$/)
  if (glued) return { prefix: glued[1], suffix: glued[2] }
  return null
}

function scoreMatch(tamName: string, locationName: string): number {
  if (compareStringsAccentInsensitive(tamName, locationName)) return 100

  const tamCompact = compactWellToken(tamName)
  const locCompact = compactWellToken(locationName)
  if (tamCompact && locCompact && tamCompact === locCompact) return 100

  // "1899" ↔ "LB 1899" (TAM a veces omite el prefijo de campo)
  const tamDigitsOnly = /^\d+[A-Z]*$/.test(tamCompact)
  const locDigitsOnly = /^\d+[A-Z]*$/.test(locCompact)
  if (tamDigitsOnly !== locDigitsOnly) {
    const tamCore = tamCompact.replace(/^[A-Z]+/, '')
    const locCore = locCompact.replace(/^[A-Z]+/, '')
    if (tamCore && locCore && tamCore === locCore) return 95
  }

  const tamParts = parseWellParts(tamName)
  const locParts = parseWellParts(locationName)
  if (tamParts && locParts) {
    if (tamParts.prefix === locParts.prefix && tamParts.suffix === locParts.suffix) return 95
    if (tamParts.prefix === locParts.prefix) {
      const tamNum = tamParts.suffix.replace(/\D/g, '')
      const locNum = locParts.suffix.replace(/\D/g, '')
      if (tamNum && locNum && tamNum === locNum && tamParts.suffix !== locParts.suffix) return 72
    }
  }

  return 0
}

export function matchTamWellToLocation(
  tamWellName: string,
  locations: SimpleLocation[],
): TamLocationMatch {
  let best: SimpleLocation | null = null
  let bestScore = 0

  for (const loc of locations) {
    const s = scoreMatch(tamWellName, loc.name)
    if (s > bestScore) {
      bestScore = s
      best = loc
    }
  }

  if (!best || bestScore < 70) {
    return { kind: 'none', location: null }
  }
  if (bestScore >= 90) {
    return { kind: 'exact', location: best }
  }
  return { kind: 'fuzzy', location: best }
}

export interface ActivityWellHint {
  pozo: string
  locationId: string
  locationName: string
}

/** Prioriza pozos ya validados en actividades del Excel antes del match genérico. */
export function matchTamWellWithHints(
  tamWellName: string,
  locations: SimpleLocation[],
  hints: ActivityWellHint[],
): TamLocationMatch {
  let bestHint: ActivityWellHint | null = null
  let bestScore = 0

  for (const hint of hints) {
    const score = Math.max(
      scoreMatch(tamWellName, hint.pozo),
      scoreMatch(tamWellName, hint.locationName),
    )
    if (score > bestScore) {
      bestScore = score
      bestHint = hint
    }
  }

  if (bestHint && bestScore >= 70) {
    const location =
      locations.find((l) => l.id === bestHint!.locationId) ??
      ({ id: bestHint.locationId, name: bestHint.locationName } as SimpleLocation)
    return {
      kind: bestScore >= 90 ? 'exact' : 'fuzzy',
      location,
    }
  }

  return matchTamWellToLocation(tamWellName, locations)
}

export function matchLabel(kind: TamLocationMatchKind): string {
  if (kind === 'exact') return 'Coincidencia exacta'
  if (kind === 'fuzzy') return 'Coincidencia aproximada'
  return 'Sin coincidencia'
}
